/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

// @ts-ignore
import convert from '@g17eco/convert-units';
import { TableColumnType, UniversalTrackerPlain, UtrValueType } from '../types/universalTracker';
import { UnitConfig } from '../model/surveyData';

const converter = convert();
type UtrUnitInfo = Pick<UniversalTrackerPlain, 'unit' | 'unitType' | 'numberScale' | 'valueType'>;

export const UnitTypes = {
  currency: 'currency',
  numberScale: 'numberScale',
};

const whitelist: {[k: string]: string[] | undefined } = {
  area: ['ha', 'km2', 'ft2', 'mi2',],
  length: ['mm', 'cm', 'm', 'km', 'in', 'yd', 'ft', 'mi'],
  mass: ['kg', 'mt', 't', 'kt'],
  volume: ['ml', 'l', 'kl', 'Ml', 'm3', 'Mcm', 'km3', 'gal', 'bbl', 'mmscf'],
  time: ['h', 'd', 'week', 'month', 'year'],
  energy: ['kWh', 'MWh', 'J', 'kJ', 'mJ', 'GJ'],
  co2Emissions: ['tons/CO2e', 'kg/CO2e'],
}

export const getUnitsForType = (type: string | undefined) => {
  const unitType = type === UnitTypes.currency ? UnitTypes.numberScale : type;
  const options = unitType ? converter.list(unitType) : []

  // Only whitelisted options
  const whitelistElement = type ? whitelist[type] : undefined;
  if (whitelistElement) {
    return options.filter(({ abbr }: { abbr: string }) => whitelistElement.includes(abbr));
  }

  // Everything
  return options;
};

/** @throws Error */
export const getUnitDescription = (code: string | undefined) => converter.describe(code);

export const convertValue = <T>(value: T, from?: string, to?: string): T => {
  if (!from || !to) {
    return value;
  }

  try {
    return convert(value).from(from).to(to);
  } catch (e) {
    return value;
  }
};


export const getUnitText = ({ valueType, unit, unitType }: UtrUnitInfo, fallback = '-'): string => {
  if (valueType === UtrValueType.Percentage) {
    return '%';
  }

  if (!unitType) {
    return fallback;
  }
  if (unitType === UnitTypes.currency) {
    return unit ?? fallback;
  }

  if (!unit) {
    return fallback;
  }

  try {
    const descr = getUnitDescription(unit);
    return descr.plural;
  } catch (e) {
    return fallback;
  }
}

export const getNumberScaleText = (numberScale: string | undefined, fallback = '-'): string => {
  if (!numberScale) {
    return fallback;
  }
  try {
    const descr = getUnitDescription(numberScale);
    return descr.plural;
  } catch (e) {
    return fallback;
  }
}

interface UnitFromTypeLookup extends  Pick<UniversalTrackerPlain, 'unitType' | 'unit'> {
  unitConfig?: UnitConfig
}
const getUnitFromUnitType = ({ unit, unitType, unitConfig }: UnitFromTypeLookup) => {
  if (!unitConfig || unitType !== UnitTypes.currency) {
    return unit;
  }
  return unitConfig.currency;
}

export const getUtrUnitInfo = (utr: UniversalTrackerPlain, tableColumn?: string, unitConfig?: UnitConfig): UtrUnitInfo | undefined => {
  if (!tableColumn) {
    return {
      valueType: utr.valueType,
      unit: getUnitFromUnitType({ unit: utr.unit, unitType: utr.unitType, unitConfig }),
      unitType: utr.unitType,
      numberScale: utr.numberScale,
    }
  }

  const data = utr.valueValidation?.table?.columns.find(col => col.code === tableColumn);
  if (!data) {
    return undefined;
  }

  // Override to percentage
  const isPercentage = data.type === TableColumnType.Number && data.validation?.min === 0 && data.validation?.max === 100;
  return {
    valueType: isPercentage ? UtrValueType.Percentage : data.type,
    unit: getUnitFromUnitType({ unit: data.unit, unitType: data.unitType, unitConfig }),
    unitType: data.unitType,
    numberScale: data.numberScale,
  }
};

export const getCurrencyList = () => {
  return [
    {
      singular: 'Euros',
      abbr: 'EUR'
    },
    {
      singular: 'British Pounds',
      abbr: 'GBP'
    },
    {
      singular: 'US Dollars',
      abbr: 'USD'
    },
    {
      singular: 'Australian Dollar',
      abbr: 'AUD'
    },
    {
      singular: 'Japanese Yen',
      abbr: 'JPY'
    },
    {
      singular: 'Yuan Renminbi',
      abbr: 'CNY'
    },
    {
      singular: 'Hong Kong Dollar',
      abbr: 'HKD'
    },
    {
      singular: 'Canadian Dollar',
      abbr: 'CAD'
    },
    {
      singular: 'Indonesian Rupiah',
      abbr: 'IDR'
    },
    {
      singular: 'Kenyan Shilling',
      abbr: 'KES'
    },
    {
      singular: 'Singapore Dollar',
      abbr: 'SGD'
    },
    {
      singular: 'Malaysian Ringgit',
      abbr: 'MYR'
    },
    {
      singular: 'Swiss Franc',
      abbr: 'CHF'
    },
    {
      singular: 'United Arab Emirates Dirham',
      abbr: 'AED',
    },
  ]
}

export const getCurrencyOptions = () => getCurrencyList().map(item => ({ value: item.abbr, label: item.singular }));
